import { Injectable } from '@angular/core';
import { CoveredLocationAdapter } from '@core/adapters/covered-location.adapter';
import { getAllFeatureFlags } from '@entities/feature-flag/feature-flag.selector';
import { TaskModel } from '@entities/task/task.model';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { ModalService } from '@shared/services/modal.service';
import { Nullable } from '@shared/utils/type.utils';
import { combineLatest, from, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { ProductModel } from '../../entities/product/product.model';
import { CoreState } from '../../reducers/index';
import * as fromActions from './covered-location.action';
import { CoveredLocationModel } from './covered-location.model';
import { getProductQuoteStatus } from '@entities/product/product.selectors';
import { HomeownerPageRepresentation } from '@core/constants/pages';
import {
  ProductType,
  QuoteStatus,
  quoteStatusGreaterThan,
} from '@core/models/api/dsm-types';
import { CoveredLocationService } from '@core/services/covered-location.service';
import { ErrorSanitizerService } from '@core/services/error-sanitizer.service';
import { ProductsService } from '@core/services/products.service';
import { ProtectiveDevicesService } from '@core/services/protective-devices.service';
import { CoveredLocationActions, TaskActions } from '@core/store/actions';
import {
  CoveredLocationSelectors,
  ProductsSelectors,
  TaskSelectors,
} from '@core/store/selectors';
import { FeatureFlagsModel } from '@entities/feature-flag/feature-flag.model';
import { CoveredLocationEntity } from './covered-location.reducer';

@Injectable()
export class CoveredLocationEffects {
  constructor(
    private actions$: Actions,
    private store: Store<CoreState>,
    private coveredLocationAdapter: CoveredLocationAdapter,
    private errorSanitizerService: ErrorSanitizerService,
    private productsService: ProductsService,
    private coveredLocationService: CoveredLocationService,
    private modalService: ModalService,
    private protectiveDevicesService: ProtectiveDevicesService
  ) {}

  getCoveredLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getCoveredLocation),
      exhaustMap((action) =>
        this.productsService.getProduct(action.payload).pipe(take(1))
      ),
      switchMap((product: Nullable<ProductModel>) => {
        return this.coveredLocationAdapter
          .getCoveredLocation(product as ProductModel)
          .pipe(
            switchMap((response) =>
              from([
                fromActions.getCoveredLocationSuccess({
                  payload: {
                    ...response,
                    productType: product?.type as ProductType,
                  },
                }),
              ])
            ),
            catchError((error) => {
              const safeError = this.errorSanitizerService.sanitizeError(
                error,
                product?.type
              );
              return from([
                fromActions.getCoveredLocationFail({ error: safeError }),
              ]);
            })
          );
      })
    )
  );

  updateCoveredLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.updateCoveredLocation),
      mergeMap((action) =>
        this.store
          .select(
            CoveredLocationSelectors.buildCoveredLocationRequest(
              action.payload,
              action.includeMsbEstimateNumber
            )
          )
          .pipe(
            take(1),
            withLatestFrom(
              this.coveredLocationService.getCoveredLocation(action.payload)
            ),
            map(([request, existingCoveredLocation]) => ({
              request,
              correlationId: action.correlationId,
              existingCoveredLocation,
            }))
          )
      ),
      mergeMap(({ request, correlationId, existingCoveredLocation }) =>
        this.coveredLocationAdapter.updateCoveredLocation(request).pipe(
          switchMap((response: CoveredLocationEntity) => {
            let wasReconstructionCostUpdated = false;
            if (
              existingCoveredLocation.wasReconstructionCostUpdated ||
              existingCoveredLocation.reconstructionCost !==
                response.reconstructionCost
            ) {
              wasReconstructionCostUpdated = true;
            }
            const nextActions: Action[] = [
              fromActions.updateCoveredLocationSuccess({
                payload: {
                  ...response,
                  wasReconstructionCostUpdated,
                  productType: request.product.type,
                },
                correlationId,
              }),
            ];
            // Any renovations blank in existingCoveredLocation but true in response were meant to be deleted.
            // updateCoveredLocation doesn't delete them.
            for (const oldRenovation of (
              existingCoveredLocation.renovations || []
            ).filter((r) => !r.year)) {
              const newRenovation = response.renovations?.find(
                (r) => r.renovationId === oldRenovation.renovationId
              );
              if (newRenovation?.year) {
                nextActions.push(
                  fromActions.deleteRenovation({
                    productType: existingCoveredLocation.productType,
                    renovationId: newRenovation.renovationId?.toString() || '',
                  })
                );
              }
            }
            return from(nextActions);
          }),
          catchError((error) => {
            const safeError = this.errorSanitizerService.sanitizeError(
              error,
              request.product.type
            );
            return from([
              fromActions.updateCoveredLocationError({
                error: safeError,
                correlationId,
              }),
            ]);
          })
        )
      )
    )
  );

  deleteRenovation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoveredLocationActions.deleteRenovation),
      mergeMap((action) =>
        this.store
          .select(ProductsSelectors.getQuoteIdForProduct(action.productType))
          .pipe(
            take(1),
            map((quoteId) => ({ action, quoteId }))
          )
      ),
      filter(({ action, quoteId }) => !!quoteId),
      mergeMap(({ action, quoteId }) => {
        return this.coveredLocationAdapter
          .deleteRenovation(action.productType, quoteId!, action.renovationId)
          .pipe(
            map(() =>
              CoveredLocationActions.deleteRenovationSuccess({
                productType: action.productType,
                renovationId: action.renovationId,
              })
            ),
            catchError((error) => {
              const saneError = this.errorSanitizerService.sanitizeError(
                error,
                action.productType
              );
              return of(
                CoveredLocationActions.deleteRenovationError({
                  productType: action.productType,
                  renovationId: action.renovationId,
                  error: saneError,
                })
              );
            })
          );
      })
    )
  );

  showLeakDetectionModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CoveredLocationActions.showLeakDetectionModal),
        tap((action) => {
          this.modalService
            .leakDetectionModal(action.productType)
            .pipe(take(1))
            .subscribe(() => {
              this.coveredLocationService.updateCoveredLocation(
                action.productType,
                false
              );
            });
        })
      ),
    { dispatch: false }
  );

  toggleTaskWhenWaterProtectionDeviceAckChanges$ = createEffect(() =>
    combineLatest([
      this.store.select(CoveredLocationSelectors.getAllCoveredLocations),
      this.store.select(TaskSelectors.selectAllTasks),
      this.store.select(getAllFeatureFlags),
      this.store.select(getProductQuoteStatus('Homeowner')),
    ]).pipe(
      switchMap(([coveredLocations, tasks, featureFlags, quoteStatus]) =>
        this.generateTaskActionsForAcknowledgement(
          coveredLocations,
          tasks,
          featureFlags,
          quoteStatus || 'Draft'
        )
      )
    )
  );

  private generateTaskActionsForAcknowledgement(
    coveredLocations: CoveredLocationModel[],
    tasks: TaskModel[],
    featureFlags: FeatureFlagsModel,
    quoteStatus: QuoteStatus
  ): Action[] {
    const actions: Action[] = [];
    if (featureFlags.showLeakDetection) {
      for (const cl of coveredLocations) {
        const task = tasks.find(
          (t) =>
            t.entityType === 'coveredLocation' &&
            t.field === 'waterProtectionDeviceRequirementCommunicated' &&
            t.productType === cl.productType
        );
        if (!task) {
          if (
            !cl.waterProtectionDeviceRequirementCommunicated &&
            quoteStatusGreaterThan(quoteStatus, 'Pending')
          ) {
            actions.push(this.acktionCreate(cl));
          }
        } else if (
          task.completed &&
          !cl.waterProtectionDeviceRequirementCommunicated
        ) {
          actions.push(this.acktionUncomplete(task));
        } else if (
          !task.completed &&
          cl.waterProtectionDeviceRequirementCommunicated
        ) {
          actions.push(this.acktionComplete(task));
        }
      }
    }
    return actions;
  }

  private acktionCreate(coveredLocation: CoveredLocationModel): Action {
    return TaskActions.addTask({
      payload: {
        page: HomeownerPageRepresentation,
        elementId: 'water-protection-device-requirement',
        field: 'waterProtectionDeviceRequirementCommunicated',
        entityType: 'coveredLocation',
        productType: coveredLocation.productType,
        ratingStatusOrdinal: 'Quoted',
        message:
          'Please review and acknowledge water protection device requirement notice.',
        resolve: CoveredLocationActions.showLeakDetectionModal({
          productType: coveredLocation.productType,
        }),
        resolveLabel: 'Review leak detection eligibility',
      },
    });
  }

  private acktionUncomplete(task: TaskModel): Action {
    return TaskActions.uncompleteTask({ payload: task });
  }

  private acktionComplete(task: TaskModel): Action {
    return TaskActions.completeTask({ payload: task });
  }
}
